import * as signalR from '@aspnet/signalr';
import {ComponentUrls, SignalREvents} from '@AppConstants';
import {appStore} from '@GlobalStores';
import {NotificationHandler} from '@Components';
import {ApiService} from './ApiService';

type TPayloadEvent<T> = (eventName: string, payload: T) => void;
type TEvent = () => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SignalREventHandler = (...args: any[]) => void;

interface ISignalRObservable {
    subscribe<T>(eventName: string, observer: TPayloadEvent<T>): void;
    unsubscribe<T>(eventName: string, observer: TPayloadEvent<T>): void;
}

type SignalRObserver = {
    eventName: string;
    observer: TEvent;
    internalObserver: SignalREventHandler;
};

class SignalRService implements ISignalRObservable {
    private _hubConnection: signalR.HubConnection | null;
    private _signalRObservers: SignalRObserver[] = [];
    private _initObservers: (() => void)[] = [];
    private _waitForConnectionPromise: Promise<void> | null;

    constructor() {
        //document.addEventListener('DOMContentLoaded', this.initialize);
        void this.initialize();
    }

    initialize = async () => {
        if (appStore.isAuthorized()) {
            await this.setUpHub();
        }
    };

    private _jwtTokenFactory() {
        return new Promise<string>((resolve, reject) => {
            if (appStore.currentTokenObj) {
                const timeMsNow = Date.now() / 1000;
                if (appStore.currentTokenObj.exp < timeMsNow) {
                    reject(new Error(`Jwt token has expired at ${appStore.currentTokenObj.exp}, current time is ${timeMsNow}`));
                    return;
                }

                resolve(appStore.currentToken);
                return;
            }

            reject(new Error('User isn\'t authorized, Jwt token is not defined'));
        });
    }

    async setUpHub() {
        const connectionUrl = window.location.origin + ComponentUrls.HubUrl;

        /* eslint-disable no-bitwise */
        const transportType = signalR.HttpTransportType.LongPolling | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.WebSockets;
        /* eslint-enable no-bitwise */
        const hubConnection = new signalR.HubConnectionBuilder().withUrl(connectionUrl, {
            transport: transportType,
            accessTokenFactory: this._jwtTokenFactory
        }).build();

        hubConnection.on(SignalREvents.success, (message: string) => {
            console.log('Success: ' + message);
        });

        hubConnection.on(SignalREvents.warning, (message: string) => {
            console.log('Warning: ' + message);
        });

        hubConnection.on(SignalREvents.error, (message: string) => {
            console.log('Error: ' + message);
        });

        hubConnection.on(SignalREvents.info, (message: string) => {
            console.log('Info: ' + message);
        });

        let reconnectMessageTimer = 0;
        let reconnect = () => {
            setTimeout(async () => {
                await this.setUpHub();
            }, 5000); // Restart connection after 5 seconds.
        };

        hubConnection.onclose(() => {
            const notificationPeriod = 1000 * 60 * 5;
            reconnectMessageTimer = window.setTimeout(() => {
                NotificationHandler.showWarning('Notifications were disconnected -> Reconnecting in 5 seconds...', 'signalReconnect');
            }, notificationPeriod);
            reconnect();
        });

        try {
            await hubConnection.start();
            console.log('SignalR connected!');

            this._hubConnection = hubConnection;
            this._signalRObservers.forEach(item => {
                hubConnection.on(item.eventName, item.internalObserver);
            });

            this._initObservers.forEach(cb => cb());

            if (reconnectMessageTimer) {
                clearTimeout(reconnectMessageTimer);
                reconnectMessageTimer = 0;
            }
        } catch (e) {
            console.log(e);
            reconnect();
        }
    }

    onInitialized(cb: () => void) {
        this._initObservers.push(cb);
        return () => this._initObservers = this._initObservers.filter(sub => sub !== cb);
    }

    public async invoke<T = void>(methodName: string, ...args: unknown[]): Promise<T> {
        await this.waitForConnection();
        if (!this._hubConnection) throw new Error('SignalR connection not established');
        return this._hubConnection.invoke<T>(methodName, ...args);
    }

    public subscribe<T>(eventName: string, observer: TPayloadEvent<T>) {
        const observerItem: SignalRObserver = {
            eventName: eventName,
            observer: observer as TEvent,
            internalObserver: (payload: T) => observer(eventName, ApiService.typeCheck(payload))
        };
        this._signalRObservers.push(observerItem);
        this._hubConnection?.on(eventName, observerItem.internalObserver);
    }

    public unsubscribe<T>(eventName: string, observer: TPayloadEvent<T>) {
        const observerRegistration = this._signalRObservers.find(item => item.eventName === eventName && item.observer === observer);
        if (!observerRegistration) {
            throw new Error(`Registration for ${eventName} is missing`);
        }

        const index = this._signalRObservers.indexOf(observerRegistration);
        this._signalRObservers.splice(index, 1);

        this._hubConnection?.off(eventName, observerRegistration.internalObserver);
    }

    public waitForConnection(timeout: number = 30000): Promise<void> {
        if (this._hubConnection?.state === signalR.HubConnectionState.Connected)
            return Promise.resolve();

        if (this._waitForConnectionPromise)
            return this._waitForConnectionPromise;

        this._waitForConnectionPromise = new Promise((resolve, reject) => {
            let checkIntervalId: number | undefined;

            const timeoutId = window.setTimeout(() => {
                window.clearInterval(checkIntervalId);
                this._waitForConnectionPromise = null;

                reject(new Error('Timeout waiting for signalR connection'));
            }, timeout);

            const checkConnection = () => {
                if (this._hubConnection && this._hubConnection.state === signalR.HubConnectionState.Connected) {
                    window.clearTimeout(timeoutId);
                    checkIntervalId && window.clearInterval(checkIntervalId);
                    this._waitForConnectionPromise = null;

                    resolve();
                    return true;
                }
                return false;
            };
            checkIntervalId = window.setInterval(checkConnection, 50);
        });
        return this._waitForConnectionPromise;
    }
}

export const signalRService = new SignalRService();